/* istanbul ignore file */
import { Environment } from "../entities/environment";
import { navStateStore } from "../state/nav-state-store";

export enum NavigationPageAction {
  NAVIGATION_LOADED = "NAVIGATION_LOADED",
  NAVIGATION_DRAWER_LOADED = "NAVIGATION_DRAWER_LOADED",
  NAVIGATION_HEADER_DEAD_CLICK = "NAVIGATION_HEADER_DEAD_CLICK",
  NAVIGATION_LOGOUT_SUCCESS = "NAVIGATION_LOGOUT_SUCCESS",
  NAVIGATION_LOGIN_SUCCESS = "NAVIGATION_LOGIN_SUCCESS",
  NAVIGATION_LOGIN = "NAVIGATION_LOGIN",
  NAVIGATION_LOGIN_ERROR = "NAVIGATION_LOGIN_ERROR",
  NAVIGATION_LOGIN_REDIRECT_ERROR = "NAVIGATION_LOGIN_REDIRECT_ERROR",
  THIRD_PARTY_LOGIN = "NAVIGATION_THIRD_PARTY_LOGIN",
  NAVIGATION_FEATURES = "NAVIGATION_FEATURES",
  NAVIGATION_SERIES_IMG_PRELOAD = "NAVIGATION_SERIES_IMG_PRELOAD",
  NAVIGATION_SERIES_IMG_PRELOAD_ERROR = "NAVIGATION_SERIES_IMG_PRELOAD_ERROR",
}

// avoid imports of MicroAgent types due to stencil/rollup import issue
interface NewrelicMicroAgent {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  MicroAgent: any;
}

export interface NavigationAgent {
  noticeError(error: Error, options: object): void;
  addPageAction(name: NavigationPageAction, attributes?: object): void;
}

declare global {
  interface Window {
    PHN_NAVIGATION_AGENT?: NavigationAgent;
  }
}

export class MicroAgentWrapper {
  /*
   * //https://github.com/ionic-team/stencil/issues/5286
   * Need to lazy load the micro agent to keep initial bundle small
   * */
  public static async lazyLoadNewRelic(): Promise<NewrelicMicroAgent> {
    return import("@newrelic/browser-agent/loaders/micro-agent");
  }

  private static setAgent(agent: NavigationAgent): void {
    Object.assign(window, { PHN_NAVIGATION_AGENT: agent });
  }

  private static getAgent(): NavigationAgent | null {
    // eslint-disable-next-line
    return window.PHN_NAVIGATION_AGENT || null;
  }

  public static async init(): Promise<NavigationAgent | null> {
    if (!MicroAgentWrapper.getAgent()) {
      const config = MicroAgentWrapper.getNewRelicConfigurationForEnvironment();
      const nr: NewrelicMicroAgent = await MicroAgentWrapper.lazyLoadNewRelic();
      if (config && nr?.MicroAgent) {
        MicroAgentWrapper.setAgent(
          new nr.MicroAgent(config) as NavigationAgent,
        );
      } else {
        return Promise.reject(
          new Error("Failed to load New Relic Micro Agent."),
        );
      }
    }

    return Promise.resolve(MicroAgentWrapper.getAgent());
  }

  public static noticeError(error: Error, options: object): void {
    const agent = MicroAgentWrapper.getAgent();
    if (agent) {
      agent.noticeError(error, options);
    }
  }

  public static addPageAction(
    name: NavigationPageAction,
    attributes?: object,
  ): void {
    const agent = MicroAgentWrapper.getAgent();
    if (agent) {
      agent.addPageAction(name, attributes);
    }
  }

  /**
   * Information can be retrieved from newrelic.com, browser (navigation-header) -> application settings
   * Check the script tag for info and loader config
   * Information is public (no secrets)
   * @private
   */
  private static getNewRelicConfigurationForEnvironment(): object | null {
    const env: Environment = navStateStore?.state?.env;
    const beacon = "bam.eu01.nr-data.net";
    const errorBeacon = "bam.eu01.nr-data.net";

    if (env === "production") {
      return {
        info: {
          beacon,
          errorBeacon,
          licenseKey: "NRJS-b184027d856e3dd7e02",
          applicationID: "538636440",
          sa: 1,
        },
        loader_config: {
          accountID: "4727658",
          trustKey: "4727658",
          agentID: "538636440",
          licenseKey: "NRJS-b184027d856e3dd7e02",
          applicationID: "538636440",
        },
      };
    }

    if (env === "preview" || env === "dev") {
      // add "local" to test locally
      return {
        info: {
          beacon,
          errorBeacon,
          licenseKey: "NRJS-592f31ba23b04dab20e",
          applicationID: "538636437",
          sa: 1,
        },
        loader_config: {
          accountID: "4727661",
          trustKey: "4727661",
          agentID: "538636437",
          licenseKey: "NRJS-592f31ba23b04dab20e",
          applicationID: "538636437",
        },
      };
    }

    return null;
  }
}
